// `mud-date-input` and `mud-numeric-input` publish what is typed through `mudInput`
// and commit it only on blur, Enter or the calendar (`mudChange`). The React
// wrapper, @lit/react, assigns every element property on every render, `value`
// included. So a parent that renders for any reason between a keystroke and the
// commit writes its own, still-old `value` back into the element, and the date
// or number being typed is reset under the reader's fingers.
//
// These two hold what the element itself holds — read from the element after
// each of its own `mudInput` and `mudChange` — keyed to the `value` the parent
// had at that moment. While the parent still has that value, the element is
// handed back its own text, which is a no-op; the moment the parent's value
// changes (its commit landed, or it was reset), the parent's value wins again.
//
// A commit that carries no value ends the hold at once. A half-typed "12/03/"
// commits with `isoValue: null`, and every caller
// reads that as "no date" (see `dateInputToIso`): the medicine form stores an
// empty expiry, a filter drops its bound. Keeping the text on screen would show
// a date the form does not hold, and Save would send the record without it. So
// the field goes back to the parent's value, as it did before the hold existed.
// A number ends its hold on blur and on any commit: an emptied, ambiguous or
// non-numeric entry either commits `null` (which each caller maps to its own
// number) or commits nothing and leaves the element showing the last typed
// number, and in both cases only the parent's value is the truth.
import { useState } from 'react';
import type { ComponentProps } from 'react';
import { MudDateInput, MudNumericInput } from '@egov-moldova/mud-react';

type Held<T> = { held: T; base: T };

function useHeldValue<T>(value: T): [T, (event: Event) => void, () => void] {
  const [draft, setDraft] = useState<Held<T> | null>(null);
  const shown = draft !== null && Object.is(draft.base, value) ? draft.held : value;
  const capture = (event: Event): void => {
    const element = event.currentTarget as unknown as { value: T };
    setDraft({ held: element.value, base: value });
  };
  const release = (): void => setDraft(null);
  return [shown, capture, release];
}

/**
 * `mud-date-input` names the date in `isoValue`, `null` when the text is not a
 * whole, valid date. The calendar's own `mudChange`, which escapes the shadow
 * root (see `onDateCommit`), carries no `isoValue` at all and says nothing either way.
 */
function commitsNoDate(event: Event): boolean {
  const detail = (event as CustomEvent<unknown>).detail;
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'isoValue' in detail &&
    (detail as { isoValue: unknown }).isoValue === null
  );
}

export type DraftDateInputProps = ComponentProps<typeof MudDateInput>;

/** A `MudDateInput` whose half-typed date survives a parent re-render. */
export function DraftDateInput({
  value = '',
  onMudInput,
  onMudChange,
  ...rest
}: DraftDateInputProps): JSX.Element {
  const [shown, capture, release] = useHeldValue(value);
  return (
    <MudDateInput
      {...rest}
      value={shown}
      onMudInput={(event) => {
        capture(event);
        onMudInput?.(event);
      }}
      onMudChange={(event) => {
        if (commitsNoDate(event)) release();
        else capture(event);
        onMudChange?.(event);
      }}
    />
  );
}

export type DraftNumericInputProps = ComponentProps<typeof MudNumericInput>;

/** A `MudNumericInput` whose half-typed number survives a parent re-render. */
export function DraftNumericInput({
  value,
  onMudInput,
  onMudChange,
  onMudBlur,
  ...rest
}: DraftNumericInputProps): JSX.Element {
  const [shown, capture, release] = useHeldValue(value);
  return (
    <MudNumericInput
      {...rest}
      {...(shown === undefined ? {} : { value: shown })}
      onMudInput={(event) => {
        capture(event);
        onMudInput?.(event);
      }}
      onMudChange={(event) => {
        release();
        onMudChange?.(event);
      }}
      onMudBlur={(event) => {
        release();
        onMudBlur?.(event);
      }}
    />
  );
}
